import { LockOpen, LockOutlined, LockReset, MailOutlined, Password, VpnLock } from "@mui/icons-material";
import { Stack, Tab, Typography } from "@mui/material";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { UserBadge } from "../../../components/Badges/UserBadge";
import ClientAssociationsComponent from "../../../components/ClientAssociationsComponent";
import { ConfirmationButton } from "../../../components/ConfirmationButton";
import { CustomTabs } from "../../../components/Forms/CustomTabs";
import Loading from "../../../components/loading";
import { DateToFormattedString } from "../../../components/Logic/DateLogic";
import UseUrlParam from "../../../components/Logic/UseUrlProps";
import RessourceMenu from "../../../components/RessourceMenu";
import ServiceAssociationsComponent from "../../../components/ServiceAssociationsComponent";
import TabPanel from "../../../components/TabPanel";
import { PaginationQueryPackage } from "../../../models/API/QueryParams/PaginationQueryPackage";
import { useGetAuthorizationsForUserQuery } from "../../AuthorisationApi/redux/authroisationApiSlice";
import { useGetClientsByUserContactQuery } from "../../ClientApi/redux/clientsApiSlice";
import { PermissionEnum } from "../../Permission/PermissionEnum";
import { useGetServicesByApproverQuery, useGetServicesByUserContactQuery } from "../../ServiceApi/redux/serviceApiSlice";
import { AbilityContext } from "../logic/Can";
import { useDeleteUserMutation, useForceNewMFAForUserMutation, useForceNewPasswordForUserMutation, useGetUserByIdQuery, useSendWelcomeMailMutation, useUpdateSslVpnAccessForUserMutation, useUpdateUserMutation } from "../redux/userApiSlice";
import AuthorisationsTab from "./tabs/AuthorisationsTab";
import InfoTab from "./tabs/InfoTab";
import { UserCopyButton } from "./UserCopyButton";

interface UserInfoDialogProps {
    userUuid: string
    afterDeleteAction?: () => void
}

export const UserInfoDialog = (props: UserInfoDialogProps | UseUrlParam) => {

    const params = useParams();
    const urlPropsUuid = (props as UseUrlParam).shouldGetEntityFromUrl ? params["userUuid"] : "";
    const userPropsUuid = (props as UserInfoDialogProps).userUuid
    const userUuid = userPropsUuid ?? urlPropsUuid;

    const forceNewMFA = useForceNewMFAForUserMutation();
    const forceNewMFATrigger = forceNewMFA[0];


    const forceNewPassword = useForceNewPasswordForUserMutation();
    const forceNewPasswordTrigger = forceNewPassword[0];

    const updateUserKeycloakEnabledStatus = useUpdateUserMutation();
    const updateUserKeycloakEnabledStatusTrigger = updateUserKeycloakEnabledStatus[0];

    const updateSslVpnForUser = useUpdateSslVpnAccessForUserMutation();
    const updateSslVpnForUserTrigger = updateSslVpnForUser[0];

    const sendWelcomeMail = useSendWelcomeMailMutation();
    const sendWelcomeMailTrigger = sendWelcomeMail[0];

    const [value, setValue] = React.useState(0);
    const { t } = useTranslation()
    const { data: user } = useGetUserByIdQuery(userUuid);
    const { data: authorisations } = useGetAuthorizationsForUserQuery({
        pagination: new PaginationQueryPackage(),
        uuid: userUuid
    })
    const ability = useContext(AbilityContext)

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    if (user == undefined || authorisations == undefined)
        return <Loading />

    const labels = []
    const tabPanel = []

    labels.push(t("Info") + "")
    tabPanel.push(<TabPanel value={value} index={tabPanel.length}><InfoTab userUuid={user.uuid!} {...props} /></TabPanel>)
    if (ability.can(PermissionEnum.AUTHORISATION_READ, user)) {
        labels.push(t("Authorisations") + "")
        tabPanel.push(<TabPanel value={value} index={tabPanel.length}><AuthorisationsTab userUuid={user.uuid!} {...props} /></TabPanel>)
    }
    if (ability.can(PermissionEnum.SERVICE_READ, user)) {
        labels.push(t("Service associations") + "")
        tabPanel.push(<TabPanel value={value} index={tabPanel.length}><ServiceAssociationsComponent
            getContactsByIdAction={useGetServicesByUserContactQuery} contactsTitle="Services with user as contact"
            getApproversByIdAction={useGetServicesByApproverQuery} approversTitle="Services with user as approver"
            uuid={user.uuid!} {...props} /></TabPanel>)
    }
    if (ability.can(PermissionEnum.CLIENT_READ, user)) {
        labels.push(t("Client associations") + "")
        tabPanel.push(<TabPanel value={value} index={tabPanel.length}><ClientAssociationsComponent
            getContactsByIdAction={useGetClientsByUserContactQuery} contactsTitle="Clients with user as contact"
            uuid={user.uuid!} {...props} /></TabPanel>)
    }

    return (
        <>
            <CustomTabs value={value} onChange={handleChange} rightMenu={
                <RessourceMenu
                    isAllowedToDelete={(data) => ability.can(PermissionEnum.USER_WRITE, data)}
                    deleteDisabled={user.is_service_account && !ability.can(PermissionEnum.GOD, user)}
                    deleteTooltip={user.is_service_account && !ability.can(PermissionEnum.GOD, user) ? t("Not supported for service account") : undefined}
                    nameOfRessource="user"
                    primaryButtonText={t("User") + ": " + user.email}
                    idToFetch={user.uuid}
                    deleteMutation={useDeleteUserMutation}
                    getByIdAction={useGetUserByIdQuery}
                    deleteButtonConfirmationText={t("Are you sure you wish to delete this user?", { "numberOfAuthorisations": authorisations.length })}
                    afterDeleteAction={(props as UserInfoDialogProps).afterDeleteAction} >
                    <>
                        {ability.can(PermissionEnum.USER_WRITE, user) && <ConfirmationButton
                            asMenuItem
                            title={user.keycloak_enabled == null ? t("User does not exist in keycloak") : t("New password")}
                            disabled={user.keycloak_enabled == null || user.is_service_account}
                            tooltip={user.is_service_account ? t("Not supported for service account") : undefined}
                            buttonText={<><Password /> {t(`New password`)}</>}
                            action={async () => {
                                await forceNewPasswordTrigger({
                                    uuid: userUuid
                                }).unwrap()
                            }}
                        >
                            <Stack spacing={2}>

                                <Stack direction="row" spacing={2}>
                                    <Typography>{t("User")}:</Typography>
                                    <UserBadge ressource={user} />
                                </Stack>

                                <Typography>{t("Are you sure you wish to force the user to change password?")}</Typography>
                                <Typography fontSize={12}>{t("The user wil be prompted next time the user logs in, or in his current session, if the user is already logged in")}</Typography>
                            </Stack>
                        </ConfirmationButton>}

                        {ability.can(PermissionEnum.USER_WRITE, user) && <ConfirmationButton
                            asMenuItem
                            title={user.keycloak_enabled == null ? t("User does not exist in keycloak") : t("New two-factor")}
                            disabled={user.keycloak_enabled == null || user.is_service_account}
                            tooltip={user.is_service_account ? t("Not supported for service account") : undefined}
                            buttonText={<><LockReset /> {t(`New two-factor`)}</>}
                            action={async () => {
                                await forceNewMFATrigger({
                                    uuid: userUuid
                                }).unwrap()
                            }}
                        >
                            <Stack spacing={2}>

                                <Stack direction="row" spacing={2}>
                                    <Typography>{t("User")}:</Typography>
                                    <UserBadge ressource={user} />
                                </Stack>

                                <Typography>{t("Are you sure you wish to force the user to reconfigure two-factor?")}</Typography>
                                <Typography fontSize={12}>{t("The user wil be prompted next time the user logs in, or in his current session, if the user is already logged in")}</Typography>
                            </Stack>
                        </ConfirmationButton>}

                        {user.keycloak_enabled !== undefined && ability.can(PermissionEnum.USER_WRITE, user) && <ConfirmationButton
                            asMenuItem
                            title={user.keycloak_enabled == null ? t("User does not exist in keycloak") : user.keycloak_enabled ? t("Disable user") : t("Enable user")}
                            disabled={user.keycloak_enabled == null || user.is_service_account}
                            tooltip={user.is_service_account ? t("Not supported for service account") : undefined}
                            buttonText={user.keycloak_enabled ?
                                <><LockOutlined /> {t("Disable user")}</>
                                :
                                <><LockOpen /> {t("Enable user")}</>
                            }
                            action={async () => {
                                await updateUserKeycloakEnabledStatusTrigger({
                                    data: {
                                        uuid: userUuid,
                                        keycloak_enabled: !user.keycloak_enabled
                                    }
                                }).unwrap()
                            }}
                        >
                            <Stack spacing={2}>

                                <Stack direction="row" spacing={2}>
                                    <Typography>{t("User")}:</Typography>
                                    <UserBadge ressource={user} />
                                </Stack>

                                <Typography>{user.keycloak_enabled ? t("Are you sure you wish to disable the user?") : t("Are you sure you wish to enable the user?")}</Typography>

                            </Stack>
                        </ConfirmationButton>}

                        {user.ssl_vpn_access !== undefined && ability.can(PermissionEnum.GOD, user) && <ConfirmationButton
                            asMenuItem
                            title={user.keycloak_enabled == null ? t("User does not exist in keycloak") : user.ssl_vpn_access ? t("Disable SSL VPN") : t("Enable SSL VPN")}
                            disabled={user.keycloak_enabled == null || user.is_service_account}
                            tooltip={user.is_service_account ? t("Not supported for service account") : undefined}
                            buttonText={user.ssl_vpn_access ?
                                <><VpnLock /> {t("Disable SSL VPN")}</>
                                :
                                <><VpnLock /> {t("Enable SSL VPN")}</>
                            }
                            action={async () => {
                                await updateSslVpnForUserTrigger({
                                    uuid: userUuid,
                                    access: !user.ssl_vpn_access
                                }).unwrap()
                            }
                            }
                        >
                            <Stack spacing={2}>

                                <Stack direction="row" spacing={2}>
                                    <Typography>{t("User")}:</Typography>
                                    <UserBadge ressource={user} />
                                </Stack>

                                <Typography>{user.ssl_vpn_access ? t("Are you sure you wish to disable SSL VPN for the user?") : t("Are you sure you wish to enable SSL VPN for the user?")}</Typography>

                            </Stack>
                        </ConfirmationButton>}

                        {ability.can(PermissionEnum.USER_WRITE, user) && <ConfirmationButton
                            asMenuItem
                            title={user.welcome_mail_sent == null ? t("Send welcome mail") : t("Resend welcome mail")}
                            disabled={user.is_service_account}
                            tooltip={user.is_service_account ? t("Not supported for service account") : undefined}
                            buttonText={<><MailOutlined /> {user.welcome_mail_sent == null ? t("Send welcome mail") : t("Resend welcome mail")}</>}
                            action={async () => {
                                await sendWelcomeMailTrigger(userUuid).unwrap()
                            }}
                        >
                            <Stack spacing={2}>

                                <Stack direction="row" spacing={2}>
                                    <Typography>{t("User")}:</Typography>
                                    <UserBadge ressource={user} />
                                </Stack>

                                <Typography>
                                    {user.welcome_mail_sent == null ?
                                        t("Are you sure you wish to send a welcome mail to the user?")
                                        :
                                        t("Are you sure you wish to resend a welcome mail to the user?")
                                    }
                                </Typography>
                                {user.welcome_mail_sent != null ?
                                    <Typography fontSize={12}>{t("Welcome mail was sent") + ": " + DateToFormattedString(user.welcome_mail_sent)}</Typography>
                                    :
                                    <></>
                                }
                            </Stack>
                        </ConfirmationButton>}

                        {ability.can(PermissionEnum.USER_WRITE, user) && <UserCopyButton user={user} />}
                    </>
                </RessourceMenu>
            }>
                {labels.map(label => (<Tab key={label} label={label} />))}
            </CustomTabs>
            {tabPanel.map(tabPanel => tabPanel)}
        </>
    )
}